import { BlockType } from '@next-space/fe-api-idl';
import type { FC, MouseEvent } from 'react';
import { Icon } from 'src/common/components/icon';
import { message } from 'src/common/components/message';
import { Tooltip } from 'src/common/components/tooltip';
import { useCreateRecord } from 'src/hooks/block/use-create-record';
import { useOpenNew } from 'src/hooks/block/use-open-new';
import { useCreateDocPage } from 'src/hooks/page';
import { useOpenPage } from 'src/hooks/page/use-open-page';
import { cache } from 'src/redux/store';
import { sequence } from 'src/utils/async-utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { isCollection } from 'src/utils/block-type-utils';
import { usePickBlock } from 'src/utils/pick-block';
import { bindDataTestId, TestIds } from 'src/utils/qa-utils';
import { useTocGroupType } from './toc-group-header';
import { NEW_SUB_PAGE_NAMES, TocType } from './types';

interface Props {
  parentId: string;
  openTocItem: () => void;
}

/** 添加子页面 */
export const AddButton: FC<Props> = ({ openTocItem, parentId }) => {
  const createDocPage = useCreateDocPage();
  // 创建多维表记录
  const createRecord = useCreateRecord();
  const parent = usePickBlock(parentId, []); // @hexiang
  const groupType = useTocGroupType();
  const openPage = useOpenPage();
  const openNew = useOpenNew();

  if (!parent) return null;

  const type = parent.type as BlockType.PAGE | BlockType.FOLDER;

  const createToc = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const parent = cache.blocks[parentId];
    if (parent?.type === BlockType.REFERENCE) {
      // feat:引用页面不支持创建子页面
      message.warning('无法为引用页面创建子页面');
      return;
    }

    bizTracker.event('subpage_new', { group_category: groupType });

    if (
      parent &&
      (isCollection(parent.type) ||
        parent.type === BlockType.REFERENCE_COLLECTION ||
        parent.type === BlockType.REFERENCE_COLLECTION_PAGE)
    ) {
      const isLinkTable =
        parent.type === BlockType.REFERENCE_COLLECTION ||
        parent.type === BlockType.REFERENCE_COLLECTION_PAGE;
      const collectionId = isLinkTable ? parent.data.ref?.uuid : parent.uuid;

      if (!collectionId) return;

      bizTracker.event('bitable_record_create', { from_scene: 'catalog' });

      const newRecordId = createRecord(collectionId);
      openPage(newRecordId);
    } else {
      const id = createDocPage(type, { parentId });
      void sequence(() => {
        openTocItem();
        openNew(id);
      });
    }
  };

  return (
    <Tooltip popup={NEW_SUB_PAGE_NAMES[type]}>
      <button
        {...bindDataTestId(
          groupType === TocType.PRIVATE
            ? TestIds.asideItemCreateByPrivate
            : TestIds.asideItemCreateBySpace
        )}
        onClick={createToc}
        className="animate-hover hidden items-center justify-center rounded-sm group-hover:flex text-grey3 w-5 h-5"
      >
        <Icon name="IcAddSmallBold" size="small" />
      </button>
    </Tooltip>
  );
};
